import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BytesPipe } from '@app/shared/pipes/bytes.pipe';
import Chart, { ActiveElement, ChartOptions } from 'chart.js/auto';
import { Subject } from 'rxjs';


@Component({
  selector: 'app-smash-chart',
  templateUrl: './smash-chart.component.html',
  styleUrls: ['./smash-chart.component.scss']
})
export class SmashChartComponent implements AfterViewInit, AfterViewChecked, OnChanges {

  @Input() data: any[];
  @Input() chartTitle: any;
  @Input() type: any;
  @Input() options: any;
  @Input() xAxisKey: string;
  @Input() xAxisType: string;
  @Input() yAxisKey: string;
  @Input() yAxisType: string;
  @Input() lineColor: string;
  @Input() displayTotal = true;

  chartCreated = new Subject<boolean>();

  chart: Chart;
  defaultChartOptions: ChartOptions = {
    onHover: this.onHover.bind(this),
    maintainAspectRatio: false,
    scales: {
      x: {
        display: false
      },
      y: {
        display: false,
        beginAtZero: true,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      filler: {
        propagate: false,
      },
      title: {
        display: true,
      },
      tooltip: {
        titleMarginBottom: 11,
        boxPadding: 10,
        callbacks: {
          title: () => this.selectedPoint.x,
          label: () => this.selectedPoint.y > 0 || typeof this.selectedPoint.y === 'string' ? this.selectedPoint.y : '0',
        },
        displayColors: false
      }
    },
    interaction: {
      intersect: false,
    },
    responsive: true,
  };
  xValues = [];
  yValues = [];
  total = 0;
  selectedPoint: any;
  contentChecked = false;

  /**
   * Whether the view (and thus the `<canvas [id]>`) exists yet. Angular sets @Input()s and fires
   * `ngOnChanges` BEFORE `ngAfterViewInit`, so building the chart on the first `data` change would
   * run `document.getElementById` before the canvas is in the DOM. Production survives this only
   * because its data arrives async (after view init); Storybook binds data at creation and would
   * throw `can't acquire context`. Gate chart building on this flag.
   */
  private viewInitialized = false;

  constructor(
    private readonly bytesPipe: BytesPipe,
    private cdRef: ChangeDetectorRef,
  ) { }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.loadDataIntoChart();
  }

  ngAfterViewChecked(): void {
    this.cdRef.detectChanges();
  }

  processData(data): void {
    this.xValues.length = 0;
    this.yValues.length = 0;
    this.total = 0;
    if (Array.isArray(data) && data.length > 0) {
      if (!!this.xAxisKey && !!this.yAxisKey) {
        data.forEach(item => {
          this.xValues.push(item[this.xAxisKey]);
          this.yValues.push(item[this.yAxisKey]);
          this.total += item[this.yAxisKey];
        });
        this.total = this.processValue(this.yAxisType, this.total);
      }
    }
  }

  loadDataIntoChart() {
    if (this.data && this.data.length) {
      this.processData(this.data);
      this.initChart();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      if (this.chart) {
        this.chart.destroy();
      }
      // Only build once the canvas exists; the first data change fires before `ngAfterViewInit`,
      // which builds the initial chart. Later data changes (post-view) rebuild here as before.
      if (this.viewInitialized) {
        this.loadDataIntoChart();
      }
    }
  }

  buildLineChart() {
    const chart = document.getElementById(this.chartId);
    this.chart = new Chart(chart as any, {
      type: 'line',
      data: {
        labels: this.xValues,
        datasets: [
          {
            data: this.yValues,
            borderColor: this.lineColor,
            fill: false,
            pointRadius: 0.05,
            pointHitRadius: 0.1,
            pointHoverRadius: 0.1,
            borderWidth: 2,
            tension: 0.1,
          },
        ],
      },
      options: this.chartOptions,
    });
  }

  initChart(): void {
    if (this.type === 'line') {
      this.processData(this.data);
      this.buildLineChart();
      this.chartCreated.next(true);
    }
  }

  processValue(type, value) {
    switch (type) {
      case 'byte':
        return this.bytesPipe.transform(value, 2);
      default:
        return value;
    }
  }

  onHover(event, activeElements: ActiveElement[]) {
    if (activeElements.length) {
      this.selectedPoint = {
        x: this.xValues[activeElements[0].index],
        y: this.processValue(this.yAxisType, this.yValues[activeElements[0].index]),
      };
    }
  }

  canvasMouseOut() {
    this.selectedPoint = null;
  }

  get chartId() {
    return 'chart-' + this.chartTitle;
  }

  get chartOptions() {
    if (!!this.options) {
      return this.options;
    } else {
      return this.defaultChartOptions;
    }
  }

}
